import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconRegistry } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import {
  AuthService,
  BREAKPOINTS,
  BulkDownloadStore,
  RouteUtilsService,
} from '@lassi/webapp-shared';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { EMPTY, switchMap, tap } from 'rxjs';
import { HeaderComponent } from './header.component';
import { InactivityService } from './inactivity/inactivity.service';

@Component({
  imports: [RouterModule, HeaderComponent, MatButtonModule, MatProgressBar],
  selector: 'lassi-root',
  template: `
    @if (isBulkDownloadLoading()) {
      <mat-progress-bar
        aria-label="Generating bulk download PDF file"
        mode="indeterminate"
      />
    }

    @if (!hideHeader()) {
      <lassi-header data-testid="global-header"></lassi-header>
    }
    <main class="content">
      <router-outlet />
    </main>
  `,
  styles: `
    mat-progress-bar {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      z-index: 999;
      margin-top: -4px;
    }

    .inactivity-banner {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      padding: 0.5rem;
      z-index: 999;
      text-align: center;
    }

    :host {
      display: flex;
      flex-direction: column;
      min-height: 100svh;
    }

    .content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  title = 'webapp';
  private readonly routeUtils = inject(RouteUtilsService);
  isMap = this.routeUtils.isMapPage;
  isRegisterPage = this.routeUtils.isRegisterPage;
  isLoginPage = this.routeUtils.isLoginPage;
  isWeb = inject(BREAKPOINTS).isWeb;

  private readonly auth = inject(AuthService);
  private readonly inactivity = inject(InactivityService);
  readonly isBulkDownloadLoading = inject(BulkDownloadStore).isLoading;

  private readonly gtmService = inject(GoogleTagManagerService);

  hideHeader = computed(
    () =>
      (this.isMap() && !this.isWeb()) ||
      this.isRegisterPage() ||
      this.isLoginPage(),
  );

  constructor() {
    this.setupInactivity();
    this.setupGTM();

    const domSanitizer = inject(DomSanitizer);
    const icons = ['google_maps', 'victoria', 'drag_zoom_in', 'drag_zoom_out'];

    const registry = inject(MatIconRegistry).setDefaultFontSetClass(
      'material-symbols-outlined',
    );
    for (const icon of icons) {
      registry.addSvgIcon(
        icon,
        domSanitizer.bypassSecurityTrustResourceUrl(
          `/assets/icons/${icon}.svg`,
        ),
      );
    }
  }

  setupGTM() {
    this.gtmService.addGtmToDom();
  }

  setupInactivity() {
    this.auth.isLoggedIn$
      .pipe(
        switchMap((isLoggedIn) => {
          if (isLoggedIn) {
            return this.inactivity
              .watch()
              .pipe(tap(() => this.handleTimeout()));
          } else {
            this.inactivity.stop();
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  handleTimeout() {
    this.auth.signout();
  }
}
